/**
 * Popup of Sandbox Pricing Solar TPO rates by partner and escalator.
 */
import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Check, CurrencyDollar, X } from '@phosphor-icons/react';
import { formatPpaRate } from '../lib/preContractPpwPricing';
import {
    formatSandboxEscalator,
    type PreContractSandboxPpaOffer,
} from '../lib/preContractSandboxPricing';
import type { PreContractSandboxPpaRatesModalProps } from './PreContractSandboxPpaRatesModal.types';

export type { PreContractSandboxPpaRatesModalProps };

function SandboxPpaRateRow({
    offer,
    selected,
    onSelect,
}: {
    offer: PreContractSandboxPpaOffer;
    selected: boolean;
    onSelect: (offer: PreContractSandboxPpaOffer) => void;
}) {
    const handleSelect = () => onSelect(offer);
    return (
        <tr className={selected ? 'pci-sandbox-rates__row--on' : undefined}>
            <td>
                <button
                    type="button"
                    className={`pci-sandbox-rates__row-btn${
                        selected ? ' pci-sandbox-rates__row-btn--on' : ''
                    }`}
                    onClick={handleSelect}
                >
                    {selected ? <Check size={12} weight="bold" /> : null}
                    {formatSandboxEscalator(offer.escalator)}
                </button>
            </td>
            <td>
                <button type="button" className="pci-sandbox-rates__cell-btn" onClick={handleSelect}>
                    {formatPpaRate(offer.startingRate)}
                </button>
            </td>
            <td>
                <button type="button" className="pci-sandbox-rates__cell-btn" onClick={handleSelect}>
                    {offer.rateCap != null ? formatPpaRate(offer.rateCap) : '—'}
                </button>
            </td>
            <td>
                <button type="button" className="pci-sandbox-rates__cell-btn" onClick={handleSelect}>
                    {offer.financeMethod || '—'}
                </button>
            </td>
            <td>
                <button type="button" className="pci-sandbox-rates__cell-btn" onClick={handleSelect}>
                    {offer.term != null ? `${offer.term} yr` : '—'}
                </button>
            </td>
        </tr>
    );
}

function groupOffersByPartner(
    offers: PreContractSandboxPpaOffer[],
): Array<{ partner: string; offers: PreContractSandboxPpaOffer[] }> {
    const groups = new Map<string, PreContractSandboxPpaOffer[]>();
    offers.forEach((offer) => {
        const list = groups.get(offer.partner) ?? [];
        list.push(offer);
        groups.set(offer.partner, list);
    });
    return Array.from(groups.entries()).map(([partner, rows]) => ({
        partner,
        offers: rows.slice().sort((a, b) => {
            if (a.escalator !== b.escalator) return a.escalator - b.escalator;
            return a.startingRate - b.startingRate;
        }),
    }));
}

export const PreContractSandboxPpaRatesModal: React.FC<
    PreContractSandboxPpaRatesModalProps
> = ({ open, offers, selectedOfferId, onSelect, onClose }) => {
    const grouped = useMemo(() => groupOffersByPartner(offers), [offers]);

    const handleSelectOffer = (offer: PreContractSandboxPpaOffer) => {
        onSelect(offer);
    };

    if (!open) return null;

    return createPortal(
        <>
            <button
                type="button"
                className="pci-pricing-modal__backdrop"
                aria-label="Close PPA rates"
                onClick={onClose}
            />
            <div
                className="pci-pricing-modal pci-pricing-modal--amber pci-sandbox-rates-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="pci-sandbox-rates-title"
            >
                <header className="pci-pricing-modal__head">
                    <div className="pci-pricing-modal__title-row">
                        <span className="pci-pricing-modal__icon" aria-hidden>
                            <CurrencyDollar size={18} weight="bold" />
                        </span>
                        <div>
                            <h2 id="pci-sandbox-rates-title" className="pci-pricing-modal__title">
                                Target/Min PPA rates
                            </h2>
                            <p className="pci-pricing-modal__subtitle">
                                Starting rate and rate cap by solar partner and escalator
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        className="pci-pricing-modal__close"
                        aria-label="Close"
                        onClick={onClose}
                    >
                        <X size={16} weight="bold" />
                    </button>
                </header>
                <div className="pci-pricing-modal__body pci-sandbox-rates">
                    {grouped.length === 0 ? (
                        <p className="pci-pricing-modal__empty">
                            No solar TPO rates were returned for this utility.
                        </p>
                    ) : (
                        grouped.map((group) => (
                            <section
                                key={group.partner}
                                className="pci-sandbox-rates__partner"
                                aria-label={`${group.partner} PPA rates`}
                            >
                                <h3 className="pci-sandbox-rates__partner-title">{group.partner}</h3>
                                <div className="pci-sandbox-rates__table-wrap">
                                    <table className="pci-sandbox-rates__table">
                                        <thead>
                                            <tr>
                                                <th scope="col">Escalator</th>
                                                <th scope="col">Starting rate</th>
                                                <th scope="col">Rate cap</th>
                                                <th scope="col">Method</th>
                                                <th scope="col">Term</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {group.offers.map((offer) => (
                                                <SandboxPpaRateRow
                                                    key={offer.id}
                                                    offer={offer}
                                                    selected={offer.id === selectedOfferId}
                                                    onSelect={handleSelectOffer}
                                                />
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </section>
                        ))
                    )}
                </div>
            </div>
        </>,
        document.body,
    );
};

export default PreContractSandboxPpaRatesModal;
